import type { PnlQueryStats } from 'src/components/star-grid/queryStats.types';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { QUERY_STATS_BYTES_PER_UNIT, QUERY_STATS_BYTE_UNITS } from 'src/components/star-grid/queryStatsConstants';
import { formatDurationMs } from 'src/components/star-grid/utils/reportStatus';

const decimal = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 });
const present = (value: number | null | undefined): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0;
export const formatQueryBytes = (bytes: number): string => {
  const unit = bytes ? Math.min(Math.floor(Math.log(bytes) / Math.log(QUERY_STATS_BYTES_PER_UNIT)), QUERY_STATS_BYTE_UNITS.length - 1) : 0;
  return `${decimal.format(bytes / QUERY_STATS_BYTES_PER_UNIT ** unit)} ${QUERY_STATS_BYTE_UNITS[unit]}`;
};
const engineDetails = (stats: PnlQueryStats | undefined, text: StarGridText): string[] => {
  if (!stats?.available) return [];
  const fields = [
    present(stats.scan_rows) && stats.scan_rows > 0 ? `${text('queryRowsScanned')}: ${compact.format(stats.scan_rows)}` : '',
    present(stats.tablets_scanned) ? `${text('queryTablets')}: ${decimal.format(stats.tablets_scanned)}` : '',
    present(stats.bytes_read) ? `${text('queryBytesRead')}: ${formatQueryBytes(stats.bytes_read)}` : '',
    present(stats.wall_time_ms) ? `${text('queryEngineWallTime')}: ${formatDurationMs(stats.wall_time_ms)}` : '',
    present(stats.cpu_time_ms) ? `${text('queryCpuTime')}: ${formatDurationMs(stats.cpu_time_ms)} (${text('queryCpuCumulative')})` : '',
    present(stats.peak_memory_bytes) ? `${text('queryPeakMemory')}: ${formatQueryBytes(stats.peak_memory_bytes)}` : ''
  ];
  return fields.filter(Boolean);
};
const catalogCount = (value?: number | null, display?: string | null): string => display?.trim() || (present(value) ? compact.format(value) : '');
const catalogDetails = (stats: PnlQueryStats | undefined, text: StarGridText): string[] => {
  const source = catalogCount(stats?.source_rows, stats?.source_rows_display);
  return [
    source ? `${text('querySourceRows')}: ${source} (${text('querySourceRowsHelp')})` : '',
    stats?.source_table ? `${text('querySourceTable')}: ${stats.source_table}` : ''
  ].filter(Boolean);
};
export const reportQueryStats = (serverMs: number | null | undefined, stats: PnlQueryStats | undefined, text: StarGridText) => {
  const server = present(serverMs) ? `${text('queryServerTime')} ${formatDurationMs(serverMs)}` : '';
  const rows =
    stats?.available && present(stats.scan_rows) && stats.scan_rows > 0 ? `${compact.format(stats.scan_rows)} ${text('queryRowsScanned')}` : '';
  return {
    inline: [server, rows].filter(Boolean).join(' · '),
    details: [server, ...catalogDetails(stats, text), ...engineDetails(stats, text)].filter(Boolean)
  };
};
